import React, { useEffect, useState } from 'react';
import {
  Drawer, Card, Descriptions, Tag, Button, Space, Spin, message,
  Modal, Timeline, Input, InputNumber, Progress, Row, Col, Statistic, Form
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { additionService } from '../services';
import {
  AdditionStatus, ADDITION_STATUS_MAP,
  ADDITION_REASON_MAP, EXECUTOR_TYPE_MAP,
  type IAddition, type IAdditionItemLog, type AdditionSubcontractSettlementMethod
} from '../types';
import { SubcontractSettlementMethod } from '../../../types';

const { TextArea } = Input;

const STATUS_ACTIONS: Record<string, { label: string; nextStatus: string; type?: 'primary' | 'default'; danger?: boolean }[]> = {
  [AdditionStatus.PENDING]: [{ label: '标记已安排', nextStatus: AdditionStatus.ASSIGNED, type: 'primary' }],
  [AdditionStatus.ASSIGNED]: [
    { label: '开始施工', nextStatus: AdditionStatus.IN_PROGRESS, type: 'primary' },
    { label: '挂起', nextStatus: AdditionStatus.SUSPENDED },
  ],
  [AdditionStatus.IN_PROGRESS]: [
    { label: '标记完工', nextStatus: AdditionStatus.COMPLETED, type: 'primary' },
    { label: '挂起', nextStatus: AdditionStatus.SUSPENDED },
  ],
  [AdditionStatus.COMPLETED]: [
    { label: '确认结算', nextStatus: AdditionStatus.SETTLED, type: 'primary' },
  ],
  [AdditionStatus.SETTLED]: [
    { label: '归档', nextStatus: AdditionStatus.ARCHIVED },
  ],
  [AdditionStatus.SUSPENDED]: [
    { label: '恢复施工', nextStatus: AdditionStatus.IN_PROGRESS, type: 'primary' },
  ],
};

const formatSettlementMethod = (method?: AdditionSubcontractSettlementMethod) => {
  if (!method) return '-';
  return method === SubcontractSettlementMethod.UNIT_PRICE_PACKAGE ? '单价包干' : '合同包干';
};

interface AdditionDetailDrawerProps {
  open: boolean;
  additionId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const AdditionDetailDrawer: React.FC<AdditionDetailDrawerProps> = ({ open, additionId, onClose, onSuccess }) => {
  const [addition, setAddition] = useState<IAddition | null>(null);
  const [logs, setLogs] = useState<IAdditionItemLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [settleModalVisible, setSettleModalVisible] = useState(false);
  const [logForm] = Form.useForm();
  const [settleForm] = Form.useForm();

  const fetchAll = async () => {
    if (!additionId) {
      setAddition(null);
      setLogs([]);
      return;
    }

    setLoading(true);
    try {
      const [addRes, logRes] = await Promise.all([
        additionService.getById(additionId),
        additionService.getLogs(additionId),
      ]);
      if (addRes.success && addRes.data) setAddition(addRes.data);
      else setAddition(null);
      if (logRes.success && logRes.data) setLogs(logRes.data);
      else setLogs([]);
    } catch (error: any) {
      message.error(error.message || '获取详情失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && additionId) {
      fetchAll();
    }
  }, [open, additionId]);

  const handleStatusChange = async (nextStatus: string) => {
    if (!additionId) return;
    if (nextStatus === AdditionStatus.SETTLED) {
      settleForm.setFieldsValue({ settlementAmount: addition?.actualAmount || addition?.estimatedAmount || 0 });
      setSettleModalVisible(true);
      return;
    }
    try {
      const res = await additionService.updateStatus(additionId, { status: nextStatus });
      if (res.success) {
        message.success('状态更新成功');
        fetchAll();
        onSuccess?.();
      }
    } catch (error: any) {
      message.error(error.message || '操作失败');
    }
  };

  const handleSettle = async () => {
    if (!additionId) return;
    try {
      const values = await settleForm.validateFields();
      const res = await additionService.updateStatus(additionId, {
        status: AdditionStatus.SETTLED,
        settlementAmount: values.settlementAmount,
        settlementRemark: values.settlementRemark,
      });
      if (res.success) {
        message.success('结算成功');
        setSettleModalVisible(false);
        fetchAll();
      }
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.message || '结算失败');
    }
  };

  const handleAddLog = async () => {
    if (!additionId) return;
    try {
      const values = await logForm.validateFields();
      const res = await additionService.addLog(additionId, values);
      if (res.success) {
        message.success('进度更新成功');
        setLogModalVisible(false);
        logForm.resetFields();
        fetchAll();
      }
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.message || '更新失败');
    }
  };

  const statusInfo = addition ? ADDITION_STATUS_MAP[addition.status] : undefined;
  const actions = addition ? (STATUS_ACTIONS[addition.status] || []) : [];
  const projectName = addition && typeof addition.projectId === 'object' ? addition.projectId.name : '-';
  const subName = addition && typeof addition.subcontractorId === 'object'
    ? addition.subcontractorId.name : addition?.subcontractorName || '-';
  const confirmedAt = addition ? ((addition as any).confirmedAt || addition.createdAt) : '';

  return (
    <>
      <Drawer
        title={addition ? `增项详情 - ${addition.additionNo}` : '增项详情'}
        open={open}
        onClose={onClose}
        width={980}
        destroyOnClose={false}
        extra={addition ? (
          <Space>
            {actions.map(a => (
              <Button key={a.nextStatus} type={a.type} danger={a.danger}
                onClick={() => handleStatusChange(a.nextStatus)}>{a.label}</Button>
            ))}
            {[AdditionStatus.ASSIGNED, AdditionStatus.IN_PROGRESS].includes(addition.status) && (
              <Button icon={<EditOutlined />} onClick={() => {
                logForm.setFieldsValue({ progressPercent: addition.progressPercent });
                setLogModalVisible(true);
              }}>更新进度</Button>
            )}
          </Space>
        ) : undefined}
      >
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>
        ) : !addition ? (
          <div style={{ padding: 24, textAlign: 'center' }}>增项不存在</div>
        ) : (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={4}><Card size="small"><Statistic title="状态" value={statusInfo?.label} /></Card></Col>
              <Col span={4}><Card size="small"><Progress type="circle" percent={addition.progressPercent} size={60} /></Card></Col>
              <Col span={4}><Card size="small"><Statistic title="增项金额" value={addition.estimatedAmount} precision={0} prefix="¥" /></Card></Col>
              <Col span={4}><Card size="small"><Statistic title="实际金额" value={addition.actualAmount} precision={0} prefix="¥" /></Card></Col>
              <Col span={4}><Card size="small"><Statistic title="结算金额" value={addition.settlementAmount || 0} precision={0} prefix="¥" /></Card></Col>
              <Col span={4}><Card size="small"><Statistic title="分包成本同步" value={addition.subcontractCostSyncStatus || '未同步'} /></Card></Col>
            </Row>

            <Card bordered={false} bodyStyle={{ padding: 0 }}>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="增项名称">{addition.title}</Descriptions.Item>
                <Descriptions.Item label="所属项目">{projectName}</Descriptions.Item>
                <Descriptions.Item label="增项原因">{ADDITION_REASON_MAP[addition.reason]}</Descriptions.Item>
                <Descriptions.Item label="类别">{addition.category}</Descriptions.Item>
                <Descriptions.Item label="现场位置">{addition.location || '-'}</Descriptions.Item>
                <Descriptions.Item label="施工员">{addition.executorName || '-'}</Descriptions.Item>
                <Descriptions.Item label="执行方式">{EXECUTOR_TYPE_MAP[addition.executorType]}</Descriptions.Item>
                <Descriptions.Item label="班组">{addition.teamName || '-'}</Descriptions.Item>
                <Descriptions.Item label="是否分包">{addition.isSubcontracted ? <Tag color="blue">是 - {subName}</Tag> : '否'}</Descriptions.Item>
                <Descriptions.Item label="分包金额">{addition.subcontractAmount != null ? `¥${addition.subcontractAmount.toLocaleString()}` : '-'}</Descriptions.Item>
                <Descriptions.Item label="分包计算方式">{formatSettlementMethod(addition.subcontractSettlementMethod)}</Descriptions.Item>
                <Descriptions.Item label="分包成本关联ID">{addition.linkedSubcontractCostId || '-'}</Descriptions.Item>
                <Descriptions.Item label="计划工期">
                  {addition.plannedStartDate ? new Date(addition.plannedStartDate).toLocaleDateString() : '-'}
                  {' ~ '}
                  {addition.plannedEndDate ? new Date(addition.plannedEndDate).toLocaleDateString() : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="实际工期">
                  {addition.actualStartDate ? new Date(addition.actualStartDate).toLocaleDateString() : '-'}
                  {' ~ '}
                  {addition.actualEndDate ? new Date(addition.actualEndDate).toLocaleDateString() : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="增项内容" span={2}>{addition.content || '-'}</Descriptions.Item>
                <Descriptions.Item label="原因说明" span={2}>{addition.reasonDescription || '-'}</Descriptions.Item>
                <Descriptions.Item label="备注" span={2}>{addition.remark || '-'}</Descriptions.Item>
                <Descriptions.Item label="录入人">{addition.createdByName}</Descriptions.Item>
                <Descriptions.Item label="增项确认时间">{confirmedAt ? new Date(confirmedAt).toLocaleString() : '-'}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="跟踪日志" style={{ marginTop: 16 }}>
              {logs.length === 0 ? <p style={{ color: '#999' }}>暂无跟踪记录</p> : (
                <Timeline>
                  {logs.map((log, idx) => (
                    <Timeline.Item key={log._id || idx} color={log.progressPercent >= 100 ? 'green' : 'blue'}>
                      <p><strong>{log.createdByName}</strong> - 进度 {log.progressPercent}%
                        {log.actualAmountIncrement ? ` | 新增金额 ¥${log.actualAmountIncrement.toLocaleString()}` : ''}</p>
                      {log.completedWork && <p>完成内容：{log.completedWork}</p>}
                      {log.issues && <p style={{ color: '#ff4d4f' }}>问题：{log.issues}</p>}
                      {log.coordinationNeeded && <p style={{ color: '#faad14' }}>需协调：{log.coordinationNeeded}</p>}
                      {log.remark && <p>备注：{log.remark}</p>}
                      <p style={{ color: '#999', fontSize: 12 }}>{new Date(log.createdAt).toLocaleString()}</p>
                    </Timeline.Item>
                  ))}
                </Timeline>
              )}
            </Card>
          </>
        )}
      </Drawer>

      <Modal title="更新进度" open={logModalVisible}
        onCancel={() => { setLogModalVisible(false); logForm.resetFields(); }}
        onOk={handleAddLog}>
        <Form form={logForm} layout="vertical">
          <Form.Item name="progressPercent" label="当前进度 %" rules={[{ required: true }]}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="completedWork" label="完成内容">
            <TextArea rows={2} placeholder="本次完成了什么" />
          </Form.Item>
          <Form.Item name="actualAmountIncrement" label="新增实际金额">
            <InputNumber min={0} precision={2} prefix="¥" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="issues" label="存在问题">
            <TextArea rows={2} placeholder="当前存在什么问题" />
          </Form.Item>
          <Form.Item name="coordinationNeeded" label="需协调事项">
            <TextArea rows={2} placeholder="需要协调什么" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input placeholder="备注" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="确认结算" open={settleModalVisible}
        onCancel={() => setSettleModalVisible(false)}
        onOk={handleSettle}>
        <Form form={settleForm} layout="vertical">
          <Form.Item name="settlementAmount" label="结算金额" rules={[{ required: true }]}>
            <InputNumber min={0} precision={2} prefix="¥" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="settlementRemark" label="结算说明">
            <TextArea rows={3} placeholder="结算说明" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AdditionDetailDrawer;
